<script setup lang="ts">
import { captureException } from '@sentry/vue';
import { doc, setDoc } from 'firebase/firestore';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import { useToast } from 'primevue/usetoast';
import { ref } from 'vue';
import { useFirestore } from 'vuefire';

const props = defineProps<{
  visible: boolean;
  userId: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const db = useFirestore();
const toast = useToast();
const handicap = ref<number | null>(null);
const isSubmitting = ref(false);

async function saveHandicap() {
  if (handicap.value === null) {
    toast.add({
      severity: 'warn',
      summary: 'Required',
      detail: 'Please enter your handicap or select "Skip for now"',
      life: 3000,
    });
    return;
  }

  try {
    isSubmitting.value = true;
    const userRef = doc(db, 'users', props.userId);
    await setDoc(userRef, { handicap: handicap.value }, { merge: true });

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Handicap saved successfully',
      life: 3000,
    });

    emit('close');
  }
  catch (error) {
    console.error('Error saving handicap:', error);
    captureException(error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Could not save handicap',
      life: 3000,
    });
  }
  finally {
    isSubmitting.value = false;
  }
}

function skipForNow() {
  emit('close');
}
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    header="Welcome to SHOTMETRICS AI!"
    :style="{ width: '450px' }"
    :closable="false"
    :close-on-escape="false"
    @update:visible="(value) => !value && emit('close')"
  >
    <div class="confirmation-content flex flex-col gap-4">
      <div>
        <h3 class="text-xl font-semibold mb-2">
          Set Your Handicap
        </h3>
        <p class="mb-4">
          Your handicap helps us provide personalized analysis, scoring in reports, and
          drill recommendations tailored to your skill level.
        </p>
        <p class="text-sm text-gray-500 mb-4">
          Don't worry, you can always update this in your profile settings later.
        </p>
      </div>

      <div class="form-group">
        <label for="handicap" class="block text-sm font-medium text-gray-700 mb-2">Your Handicap</label>
        <InputNumber
          id="handicap"
          v-model="handicap"
          class="w-full"
          :min="-10"
          :max="54"
          placeholder="Enter your handicap"
          show-buttons
          :step="0.1"
        />
      </div>
    </div>
    <template #footer>
      <div class="flex flex-col sm:flex-row gap-3 w-full justify-center sm:justify-end">
        <Button
          label="Skip for now"
          class="p-button-text"
          :disabled="isSubmitting"
          @click="skipForNow"
        />
        <Button
          label="Save"
          severity="primary"
          :loading="isSubmitting"
          autofocus
          @click="saveHandicap"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
:deep(.p-inputtext) {
  width: 100%;
}

:deep(.p-inputnumber) {
  width: 100%;
}

:deep(.p-inputnumber-input) {
  width: 100%;
}

:deep(.p-dialog-mask) {
  background-color: rgba(0, 0, 0, 0.7);
}

:deep(.p-dialog) {
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

:deep(.p-dialog-footer) {
  border-top: 1px solid var(--surface-200);
  padding-top: 1rem;
}
</style>
